import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Spinner from '../UI/Spinner';
import { BotIcon } from '../UI/Icons';

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const emailParam = searchParams.get('email');
  
  const [email, setEmail] = useState(emailParam || '');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  // 'error' from useAuth() is for general auth errors (login, logout, token refresh, and now password reset)
  // 'isLoading' is also from useAuth() for global loading state
  const { currentUser, loginWithEmail, sendPasswordResetEmail, isLoading, error: authError } = useAuth();

  // Local error state specifically for the reset form input validation
  const [resetFormError, setResetFormError] = useState<string | null>(null);

  // Navigate to dashboard when user is authenticated
  useEffect(() => {
    if (currentUser && !isLoading) {
      console.log("LoginScreen: User authenticated, navigating to dashboard");
      const redirectPath = inviteToken ? `/?invite=${inviteToken}` : '/';
      navigate(redirectPath, { replace: true });
    }
  }, [currentUser, isLoading, navigate, inviteToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("LoginScreen: Form submitted with email:", email);
    setResetMessage(null);
    setResetFormError(null); // Clear reset form error
    // Clear global auth error if any, before attempting login
    // Note: AuthContext's loginWithEmail already sets error to null at the start.
    try {
      console.log("LoginScreen: Calling loginWithEmail...");
      await loginWithEmail(email, password);
      console.log("LoginScreen: Login successful");
    } catch (submitError: any) {
      // authError will be set by loginWithEmail via AuthContext
      console.log("LoginScreen handleSubmit error:", submitError);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage(null);
    setResetFormError(null); // Clear local form error
    // Note: AuthContext's sendPasswordResetEmail also sets its 'error' (authError here) to null at the start.

    if (!resetEmail) {
      setResetFormError("Please enter your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(resetEmail);
      setResetMessage("If an account exists for this email, a password reset link has been sent. Please check your inbox (and spam folder).");
    } catch (resetError: any) {
      // authError will be set by sendPasswordResetEmail via AuthContext.
      // We can display authError directly, or use resetMessage for more specific feedback.
      // If sendPasswordResetEmail in AuthContext sets a generic "user not found" message to its error state,
      // that will be displayed as authError.
      setResetMessage(authError || "Failed to send reset email. Please try again.");
      console.log("LoginScreen handlePasswordReset error:", resetError);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="max-w-md mx-auto bg-white p-10 rounded-xl shadow-2xl">
        <div>
          <div className="mx-auto h-16 w-16 text-green-600 flex items-center justify-center">
             <BotIcon className="h-12 w-12" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your Dashboard
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your WhatsApp Business Dashboard
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <input type="hidden" name="remember" defaultValue="true" />
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {authError && !showResetForm && ( // Only show general auth errors if not in reset mode
            <p className="text-sm text-red-600 text-center">{authError}</p>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading || showResetForm} // Disable if loading or reset form is shown
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
            >
              {isLoading && !showResetForm ? <Spinner size="sm" color="border-white"/> : 'Sign in'}
            </button>
          </div>
        </form>

        <div className="text-sm text-center">
          <button
            onClick={() => {
              setShowResetForm(!showResetForm);
              setResetMessage(null);
              setResetFormError(null);
              // We don't need to manually clear authError here,
              // as new auth actions (login, reset) will manage it via AuthContext.
            }}
            className="font-medium text-green-600 hover:text-green-500"
          >
            {showResetForm ? 'Back to Sign In' : 'Forgot your password?'}
          </button>
        </div>

        {!showResetForm && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="font-medium text-green-600 hover:text-green-500">
                Sign up
              </Link>
            </p>
          </div>
        )}

        {showResetForm && (
          <form className="mt-4 space-y-4" onSubmit={handlePasswordReset}>
            <div>
              <label htmlFor="reset-email" className="sr-only">
                Email address for password reset
              </label>
              <input
                id="reset-email"
                name="reset-email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email to reset password"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>
            {resetFormError && (
              <p className="text-sm text-red-600 text-center">{resetFormError}</p>
            )}
            {resetMessage && ( // For success or error messages from the reset attempt
              <p className={`text-sm ${authError && !resetFormError ? 'text-red-600' : 'text-green-700'} text-center`}>{resetMessage}</p>
            )}
            {authError && showResetForm && !resetMessage && !resetFormError && ( // Display general auth error if reset form is active and no other message shown
                 <p className="text-sm text-red-600 text-center">{authError}</p>
            )}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
              >
                {isLoading ? <Spinner size="sm" color="border-white"/> : 'Send Reset Link'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;